import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
    TrendingUp, TrendingDown, Target, Wallet, Filter, Loader2, Download,
    Search, Check, ChevronDown, ChevronLeft, ChevronRight, X, RefreshCw,
    Calendar, Building2, Users, Truck, ShoppingCart, Receipt, FileText,
    HardHat, Banknote, Printer, ArrowDownCircle, ArrowUpCircle,
    AlertCircle, ShieldCheck, Clock, Eye, ExternalLink, Activity
} from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../api/axiosInstance';
import { AuthContext } from '../App';

// Palette for cost breakdowns
const COST_COLORS = {
    employee: '#8B5CF6',   // Purple / Labour
    vendor: '#4F46E5',     // Indigo / Vendor
    petty_cash: '#10B981', // Emerald / Petty Cash
    material: '#F59E0B',   // Amber / Direct Materials
};

// Date math helpers
const getYesterdayDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
};

const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
};

const stepDate = (currentDateStr, deltaDays) => {
    if (!currentDateStr) return getYesterdayDate();
    const [y, m, d] = currentDateStr.split('-').map(Number);
    const dateObj = new Date(y, m - 1, d);
    dateObj.setDate(dateObj.getDate() + deltaDays);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        const [y, m, d] = dateStr.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        return dt.toLocaleDateString('en-IN', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    } catch {
        return dateStr;
    }
};

export default function DailyFinancialActivity() {
    const { user } = useContext(AuthContext);

    // Filter controls: defaults to Yesterday
    const [selectedDate, setSelectedDate] = useState(getYesterdayDate());
    const [selectedProject, setSelectedProject] = useState('all'); // 'all' or project_id string
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'income' | 'expenses' | 'employee' | 'vendor' | 'petty' | 'material'
    const [tableSearch, setTableSearch] = useState('');

    // Data states
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [projectsList, setProjectsList] = useState([]);
    const [activityData, setActivityData] = useState({
        date: getYesterdayDate(),
        kpis: {
            total_income: 0,
            total_expense: 0,
            net_flow: 0,
            labour_expense: 0,
            vendor_expense: 0,
            petty_cash_expense: 0,
            material_expense: 0,
            workforce_count: 0,
            counts: {
                income_records: 0,
                employee_records: 0,
                vendor_records: 0,
                petty_records: 0,
                material_records: 0,
                total_expense_records: 0
            }
        },
        records: {
            income: [],
            employee_labour: [],
            vendor_payments: [],
            petty_cash: [],
            material_spent: [],
            all_expenses: [],
            site_breakdown: []
        }
    });

    const isYesterday = selectedDate === getYesterdayDate();
    const isToday = selectedDate === getTodayDate();

    // Load available projects once on mount
    useEffect(() => {
        api.get('get_projects.php')
            .then(res => {
                if (res.data && res.data.projects) {
                    setProjectsList(res.data.projects);
                }
            })
            .catch(err => console.warn('Could not load projects:', err));
    }, []);

    // Helper: Autonomous Client-Side Fallback Aggregator for exact date
    const fallbackAggregateDailyData = async (targetDate, targetProjId) => {
        try {
            const isAllSites = targetProjId === 'all' || !targetProjId;
            const targetProjNum = isAllSites ? null : Number(targetProjId);

            // Fetch live data from remote endpoints
            const [
                projRes,
                empRes,
                attRes,
                clientInvRes,
                vendorInvRes,
                matRes,
                pettyRes
            ] = await Promise.all([
                api.get('get_projects.php').catch(() => ({ data: { projects: [] } })),
                api.get('get_employees.php').catch(() => ({ data: { employees: [] } })),
                api.get(`get_attendance_by_date.php?date=${targetDate}`).catch(() => ({ data: { attendance: [] } })),
                api.get('get_client_invoices.php').catch(() => ({ data: { data: [] } })),
                api.get('get_all_vendor_invoices.php').catch(() => ({ data: { data: [] } })),
                api.get('get_material_purchases.php').catch(() => ({ data: { purchases: [] } })),
                api.get('get_admin_petty_cash.php').catch(() => api.get('get_petty_cash.php').catch(() => ({ data: { logs: [] } })))
            ]);

            const projects = projRes.data?.projects || projectsList;
            if (projects.length > 0 && projectsList.length === 0) {
                setProjectsList(projects);
            }
            const employees = empRes.data?.employees || [];
            const attendanceList = attRes.data?.attendance || [];
            const clientInvoices = clientInvRes.data?.data || [];
            const vendorInvoices = vendorInvRes.data?.data || [];
            const materials = matRes.data?.purchases || [];
            const pettyLogs = pettyRes.data?.logs || [];

            // Project dictionary
            const projectMap = {};
            projects.forEach(p => {
                projectMap[p.id] = p.project_name;
            });

            // Employee dictionary
            const employeeMap = {};
            employees.forEach(e => {
                employeeMap[e.id] = e;
            });

            // 1. Process Income on targetDate
            const incomeList = [];
            let totalIncome = 0;

            clientInvoices.forEach(inv => {
                const pId = inv.project_id;
                if (!isAllSites && Number(pId) !== targetProjNum) return;
                const pName = inv.project_name || projectMap[pId] || 'Project';

                // Check nested received payments
                if (Array.isArray(inv.received_payments) && inv.received_payments.length > 0) {
                    inv.received_payments.forEach(pay => {
                        const payDate = (pay.payment_date || pay.date || '').slice(0, 10);
                        if (payDate === targetDate) {
                            const amt = parseFloat(pay.amount || pay.received_amount || 0);
                            if (amt > 0) {
                                totalIncome += amt;
                                incomeList.push({
                                    id: pay.id || `inc_${inv.id}_${payDate}`,
                                    amount: amt,
                                    tds_deducted: parseFloat(pay.tds_deducted || 0),
                                    payment_date: targetDate,
                                    payment_mode: pay.payment_mode || pay.mode || 'Bank Transfer',
                                    transaction_ref: pay.transaction_ref || pay.reference || inv.invoice_no,
                                    notes: pay.notes || `Client payment for Inv #${inv.invoice_no}`,
                                    receipt_copy_path: pay.receipt_copy_path || pay.attachment || null,
                                    invoice_no: inv.invoice_no,
                                    invoice_amount: parseFloat(inv.total_amount || 0),
                                    client_name: inv.client_name || 'Client',
                                    project_id: pId,
                                    project_name: pName
                                });
                            }
                        }
                    });
                } else {
                    // Check top-level invoice payment date
                    const invPayDate = (inv.payment_date || inv.date || inv.invoice_date || '').slice(0, 10);
                    if (invPayDate === targetDate) {
                        const amt = parseFloat(inv.paid_amount || inv.amount_paid || inv.total_amount || 0);
                        if (amt > 0) {
                            totalIncome += amt;
                            incomeList.push({
                                id: `inc_${inv.id}`,
                                amount: amt,
                                tds_deducted: 0,
                                payment_date: targetDate,
                                payment_mode: inv.payment_mode || 'Direct Receipt',
                                transaction_ref: inv.invoice_no,
                                notes: `Client Invoice #${inv.invoice_no}`,
                                receipt_copy_path: null,
                                invoice_no: inv.invoice_no,
                                invoice_amount: parseFloat(inv.total_amount || 0),
                                client_name: inv.client_name || 'Client',
                                project_id: pId,
                                project_name: pName
                            });
                        }
                    }
                }
            });

            // 2. Process Employee Attendance & Daily Wages on targetDate
            const employeeRecords = [];
            let totalLabourCost = 0;
            let workersPresentCount = 0;

            const daysInMonth = (intVal => (intVal <= 0 ? 30 : intVal))(
                new Date(new Date(targetDate).getFullYear(), new Date(targetDate).getMonth() + 1, 0).getDate()
            );

            attendanceList.forEach(att => {
                const empId = att.employee_id;
                const emp = employeeMap[empId] || {};
                const attProjId = att.project_id || emp.assigned_site_id;
                
                if (!isAllSites && Number(attProjId) !== targetProjNum) return;

                const status = att.status || 'Present';
                const mult = att.multiplier !== undefined && att.multiplier !== null ? parseFloat(att.multiplier) : (status === 'Absent' ? 0 : 1.0);
                const payType = emp.pay_type || 'Daily';
                const rate = parseFloat(att.applied_rate > 0 ? att.applied_rate : (emp.rate || 0));
                let cost = 0;
                let hoursWorked = 0;

                if (status !== 'Absent' && mult > 0) {
                    workersPresentCount++;
                    if (payType === 'Hourly') {
                        if (att.in_time && att.out_time) {
                            const [h1, m1] = att.in_time.split(':').map(Number);
                            const [h2, m2] = att.out_time.split(':').map(Number);
                            let dur = (h2 + m2 / 60) - (h1 + m1 / 60);
                            if (dur < 0) dur += 24;
                            hoursWorked = dur;
                            cost = dur * rate;
                        } else {
                            hoursWorked = mult * 8;
                            cost = hoursWorked * rate;
                        }
                    } else if (payType === 'Monthly') {
                        const dailyRate = rate / daysInMonth;
                        cost = mult * dailyRate;
                    } else {
                        // Daily
                        cost = mult * rate;
                    }
                }

                cost = Math.round(cost * 100) / 100;
                totalLabourCost += cost;

                const workloadStr = payType === 'Hourly'
                    ? `${hoursWorked.toFixed(1)} Hours`
                    : (mult > 0 ? `${mult.toFixed(1)} Shift(s)` : 'Absent');

                const pName = projectMap[attProjId] || emp.assigned_site_name || 'General Site';

                employeeRecords.push({
                    id: `att_${empId}_${targetDate}`,
                    attendance_id: att.id || 0,
                    type: 'Daily Labour Wage',
                    date: targetDate,
                    employee_id: empId,
                    employee_name: emp.name || `Worker #${empId}`,
                    status: status,
                    pay_type: payType,
                    applied_rate: rate,
                    multiplier: mult,
                    in_time: att.in_time || null,
                    out_time: att.out_time || null,
                    workload: workloadStr,
                    amount: cost,
                    project_id: attProjId,
                    project_name: pName,
                    notes: `Logged attendance on ${pName} (${workloadStr})`
                });
            });

            // Also check project profitability labour_details for this exact date as a secondary check
            if (employeeRecords.length === 0) {
                try {
                    const targetProjectsForProf = projects.filter(p => isAllSites || Number(p.id) === targetProjNum);
                    const profResponses = await Promise.all(
                        targetProjectsForProf.map(p => api.get(`get_project_profitabilitynew.php?project_id=${p.id}`).catch(() => null))
                    );

                    profResponses.forEach((res, idx) => {
                        const proj = targetProjectsForProf[idx];
                        if (!proj || !res?.data?.details?.labour_details) return;
                        const lList = res.data.details.labour_details;

                        lList.forEach((row, rIdx) => {
                            const rowDate = (row.date || '').slice(0, 10);
                            if (rowDate === targetDate) {
                                const amt = parseFloat(row.amount || 0);
                                if (amt > 0) {
                                    totalLabourCost += amt;
                                    workersPresentCount++;
                                    employeeRecords.push({
                                        id: `prof_lab_${proj.id}_${rIdx}`,
                                        attendance_id: 0,
                                        type: 'Daily Labour Wage',
                                        date: targetDate,
                                        employee_id: 0,
                                        employee_name: row.description || 'Site Labour',
                                        status: 'Present',
                                        pay_type: 'Daily Wage',
                                        applied_rate: amt,
                                        multiplier: 1,
                                        in_time: null,
                                        out_time: null,
                                        workload: '1 Shift',
                                        amount: amt,
                                        project_id: proj.id,
                                        project_name: proj.project_name,
                                        notes: `Labour booked on ${proj.project_name}`
                                    });
                                }
                            }
                        });
                    });
                } catch (profErr) {
                    console.warn('Could not query profitability details for date:', profErr);
                }
            }

            // 3. Process Vendor Payments on targetDate
            const vendorList = [];
            let totalVendorCost = 0;

            vendorInvoices.forEach(vi => {
                const pId = vi.project_id;
                if (!isAllSites && Number(pId) !== targetProjNum) return;
                const pName = vi.project_name || projectMap[pId] || 'Project';

                // Check nested payments
                if (Array.isArray(vi.payments) && vi.payments.length > 0) {
                    vi.payments.forEach(vp => {
                        const vpDate = (vp.payment_date || vp.date || '').slice(0, 10);
                        if (vpDate === targetDate) {
                            const amt = parseFloat(vp.amount_paid || vp.amount || 0);
                            if (amt > 0) {
                                totalVendorCost += amt;
                                vendorList.push({
                                    id: vp.id || `vp_${vi.id}_${vpDate}`,
                                    payment_date: targetDate,
                                    amount: amt,
                                    payment_method: vp.payment_method || 'Bank Transfer',
                                    notes: vp.notes || `Payment for Inv #${vi.invoice_no}`,
                                    attachment_path: vp.attachment || vp.receipt_path || null,
                                    invoice_no: vi.invoice_no,
                                    invoice_amount: parseFloat(vi.total_amount || 0),
                                    vendor_name: vi.vendor_name || 'Vendor',
                                    project_id: pId,
                                    project_name: pName
                                });
                            }
                        }
                    });
                } else {
                    const viDate = (vi.payment_date || vi.invoice_date || vi.created_at || '').slice(0, 10);
                    if (viDate === targetDate) {
                        const amt = parseFloat(vi.paid_amount || vi.total_amount || 0);
                        if (amt > 0) {
                            totalVendorCost += amt;
                            vendorList.push({
                                id: `vi_${vi.id}`,
                                payment_date: targetDate,
                                amount: amt,
                                payment_method: 'NEFT / RTGS',
                                notes: `Vendor Bill #${vi.invoice_no}`,
                                attachment_path: null,
                                invoice_no: vi.invoice_no,
                                invoice_amount: parseFloat(vi.total_amount || 0),
                                vendor_name: vi.vendor_name || 'Vendor Partner',
                                project_id: pId,
                                project_name: pName
                            });
                        }
                    }
                }
            });

            // 4. Process Petty Cash on targetDate
            const pettyList = [];
            let totalPettyCost = 0;

            pettyLogs.forEach(pc => {
                const pId = pc.project_id;
                if (!isAllSites && Number(pId) !== targetProjNum) return;
                const pcDate = (pc.date || pc.created_at || '').slice(0, 10);

                if (pcDate === targetDate) {
                    const isDebit = !pc.type || pc.type.toLowerCase() === 'debit';
                    if (isDebit) {
                        const amt = parseFloat(pc.amount || 0);
                        if (amt > 0) {
                            totalPettyCost += amt;
                            pettyList.push({
                                id: pc.id || `pc_${pcDate}_${Math.random()}`,
                                date: targetDate,
                                amount: amt,
                                category: pc.category || 'General',
                                remarks: pc.remarks || pc.purpose || 'Site Petty Cash',
                                status: pc.status || 'Approved',
                                bill_image: pc.bill_image || pc.attachment || null,
                                staff_name: pc.staff_name || pc.user_name || pc.username || 'Site Staff',
                                project_id: pId,
                                project_name: pc.project_name || projectMap[pId] || 'Site Petty Cash'
                            });
                        }
                    }
                }
            });

            // 5. Process Material Spent on targetDate
            const materialList = [];
            let totalMaterialCost = 0;

            materials.forEach(mp => {
                const pId = mp.project_id;
                if (!isAllSites && Number(pId) !== targetProjNum) return;
                const mDate = (mp.purchase_date || mp.date || mp.created_at || '').slice(0, 10);

                if (mDate === targetDate) {
                    const amt = parseFloat(mp.amount || 0);
                    if (amt > 0) {
                        totalMaterialCost += amt;
                        materialList.push({
                            id: mp.id || `mp_${mDate}_${Math.random()}`,
                            date: targetDate,
                            amount: amt,
                            biller_name: mp.biller_name || mp.supplier || 'Local Supplier',
                            items_summary: mp.items_summary || mp.description || 'Site Materials',
                            paid_by: mp.paid_by || 'Staff',
                            payment_method: mp.payment_method || 'Direct / Cash',
                            status: mp.status || 'Verified',
                            bill_screenshot: mp.bill_screenshot || mp.attachment || null,
                            project_id: pId,
                            project_name: mp.project_name || projectMap[pId] || 'Direct Procurement'
                        });
                    }
                }
            });

            // 6. Synthesize Consolidated Daily Expenses Stream
            const allExpenses = [];

            employeeRecords.forEach(emp => {
                if (emp.amount > 0) {
                    allExpenses.push({
                        id: emp.id,
                        category: 'Employee / Labour',
                        date: targetDate,
                        entity_name: emp.employee_name,
                        project_id: emp.project_id,
                        project_name: emp.project_name,
                        amount: emp.amount,
                        payment_method: emp.pay_type,
                        description: emp.notes,
                        proof_url: null
                    });
                }
            });

            vendorList.forEach(ven => {
                allExpenses.push({
                    id: `ven_${ven.id}`,
                    category: 'Vendor Payment',
                    date: targetDate,
                    entity_name: ven.vendor_name,
                    project_id: ven.project_id,
                    project_name: ven.project_name,
                    amount: ven.amount,
                    payment_method: ven.payment_method,
                    description: `Invoice #${ven.invoice_no} - ${ven.notes}`,
                    proof_url: ven.attachment_path
                });
            });

            pettyList.forEach(petty => {
                allExpenses.push({
                    id: `petty_${petty.id}`,
                    category: 'Petty Cash',
                    date: targetDate,
                    entity_name: petty.staff_name,
                    project_id: petty.project_id,
                    project_name: petty.project_name,
                    amount: petty.amount,
                    payment_method: 'Cash',
                    description: `[${petty.category}] ${petty.remarks}`,
                    proof_url: petty.bill_image
                });
            });

            materialList.forEach(mat => {
                allExpenses.push({
                    id: `mat_${mat.id}`,
                    category: 'Material Spent',
                    date: targetDate,
                    entity_name: mat.biller_name,
                    project_id: mat.project_id,
                    project_name: mat.project_name,
                    amount: mat.amount,
                    payment_method: mat.payment_method,
                    description: mat.items_summary,
                    proof_url: mat.bill_screenshot
                });
            });

            allExpenses.sort((a, b) => b.amount - a.amount);

            const totalExpenses = totalLabourCost + totalVendorCost + totalPettyCost + totalMaterialCost;
            const netFlow = totalIncome - totalExpenses;

            // Site-by-site breakdown
            const siteBreakdownMap = {};
            projects.forEach(p => {
                siteBreakdownMap[p.id] = {
                    project_id: p.id,
                    project_name: p.project_name,
                    income: 0,
                    labour: 0,
                    vendor: 0,
                    petty_cash: 0,
                    material: 0,
                    total_expense: 0,
                    workforce_count: 0
                };
            });

            incomeList.forEach(inc => {
                if (siteBreakdownMap[inc.project_id]) siteBreakdownMap[inc.project_id].income += inc.amount;
            });
            employeeRecords.forEach(emp => {
                if (siteBreakdownMap[emp.project_id]) {
                    siteBreakdownMap[emp.project_id].labour += emp.amount;
                    if (emp.status !== 'Absent' && emp.multiplier > 0) {
                        siteBreakdownMap[emp.project_id].workforce_count++;
                    }
                }
            });
            vendorList.forEach(ven => {
                if (siteBreakdownMap[ven.project_id]) siteBreakdownMap[ven.project_id].vendor += ven.amount;
            });
            pettyList.forEach(petty => {
                if (siteBreakdownMap[petty.project_id]) siteBreakdownMap[petty.project_id].petty_cash += petty.amount;
            });
            materialList.forEach(mat => {
                if (siteBreakdownMap[mat.project_id]) siteBreakdownMap[mat.project_id].material += mat.amount;
            });

            const activeSites = Object.values(siteBreakdownMap).filter(s => {
                s.total_expense = s.labour + s.vendor + s.petty_cash + s.material;
                if (!isAllSites) return Number(s.project_id) === targetProjNum;
                return (s.income > 0 || s.total_expense > 0 || s.workforce_count > 0);
            });

            setActivityData({
                date: targetDate,
                kpis: {
                    total_income: Math.round(totalIncome),
                    total_expense: Math.round(totalExpenses),
                    net_flow: Math.round(netFlow),
                    labour_expense: Math.round(totalLabourCost),
                    vendor_expense: Math.round(totalVendorCost),
                    petty_cash_expense: Math.round(totalPettyCost),
                    material_expense: Math.round(totalMaterialCost),
                    workforce_count: workersPresentCount,
                    counts: {
                        income_records: incomeList.length,
                        employee_records: employeeRecords.length,
                        vendor_records: vendorList.length,
                        petty_records: pettyList.length,
                        material_records: materialList.length,
                        total_expense_records: allExpenses.length
                    }
                },
                records: {
                    income: incomeList,
                    employee_labour: employeeRecords,
                    vendor_payments: vendorList,
                    petty_cash: pettyList,
                    material_spent: materialList,
                    all_expenses: allExpenses,
                    site_breakdown: activeSites
                }
            });
        } catch (err) {
            console.error('Fallback daily aggregator error:', err);
        }
    };

    // Primary Fetch Function
    const fetchDailyActivity = async () => {
        setLoading(true);
        try {
            const res = await api.get(`get_daily_activity.php?date=${selectedDate}&project_id=${selectedProject}`);

            if (res.data && res.data.status === 'success') {
                setActivityData({
                    date: res.data.date || selectedDate,
                    kpis: res.data.kpis || {},
                    records: res.data.records || {}
                });
                if (res.data.projects_list && res.data.projects_list.length > 0) {
                    setProjectsList(res.data.projects_list);
                }
            } else {
                await fallbackAggregateDailyData(selectedDate, selectedProject);
            }
        } catch (err) {
            console.warn('Dedicated get_daily_activity.php not reachable, activating autonomous fallback:', err);
            await fallbackAggregateDailyData(selectedDate, selectedProject);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // Refetch whenever selectedDate or selectedProject changes
    useEffect(() => {
        fetchDailyActivity();
    }, [selectedDate, selectedProject]);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchDailyActivity();
    };

    // Date Step Helpers
    const handlePrevDay = () => {
        setSelectedDate(prev => stepDate(prev, -1));
    };

    const handleNextDay = () => {
        setSelectedDate(prev => stepDate(prev, 1));
    };

    const handleSetYesterday = () => {
        setSelectedDate(getYesterdayDate());
    };

    const handleSetToday = () => {
        setSelectedDate(getTodayDate());
    };

    // Currency Formatter
    const formatINR = (val) => {
        const num = Number(val) || 0;
        return `₹${num.toLocaleString('en-IN')}`;
    };

    // Search filter helper
    const filterRows = (rows, keys) => {
        if (!tableSearch.trim()) return rows;
        const q = tableSearch.toLowerCase();
        return (rows || []).filter(r =>
            keys.some(k => {
                const val = r[k];
                return val && String(val).toLowerCase().includes(q);
            })
        );
    };

    const filteredIncome = useMemo(() =>
        filterRows(activityData.records?.income || [], ['client_name', 'project_name', 'invoice_no', 'payment_mode', 'transaction_ref', 'notes']),
        [activityData.records?.income, tableSearch]
    );

    const filteredAllExpenses = useMemo(() =>
        filterRows(activityData.records?.all_expenses || [], ['category', 'entity_name', 'project_name', 'description', 'payment_method']),
        [activityData.records?.all_expenses, tableSearch]
    );

    const filteredEmployeeLabour = useMemo(() =>
        filterRows(activityData.records?.employee_labour || [], ['employee_name', 'project_name', 'pay_type', 'status', 'workload', 'notes']),
        [activityData.records?.employee_labour, tableSearch]
    );

    const filteredVendorPayments = useMemo(() =>
        filterRows(activityData.records?.vendor_payments || [], ['vendor_name', 'project_name', 'invoice_no', 'payment_method', 'notes']),
        [activityData.records?.vendor_payments, tableSearch]
    );

    const filteredPettyCash = useMemo(() =>
        filterRows(activityData.records?.petty_cash || [], ['category', 'remarks', 'staff_name', 'project_name', 'status']),
        [activityData.records?.petty_cash, tableSearch]
    );

    const filteredMaterialSpent = useMemo(() =>
        filterRows(activityData.records?.material_spent || [], ['biller_name', 'items_summary', 'project_name', 'paid_by', 'payment_method']),
        [activityData.records?.material_spent, tableSearch]
    );

    // Export Daily Log to Excel
    const handleExportExcel = () => {
        try {
            const wb = XLSX.utils.book_new();

            // Summary Sheet
            const summarySheetData = [
                { Metric: 'Audit Date', Value: selectedDate },
                { Metric: 'Day Name', Value: formatDisplayDate(selectedDate) },
                { Metric: 'Site Filter', Value: selectedProject === 'all' ? 'All Sites / Projects' : (projectsList.find(p => String(p.id) === String(selectedProject))?.project_name || selectedProject) },
                { Metric: 'Total Daily Inflow (Income)', Value: activityData.kpis.total_income },
                { Metric: 'Total Daily Outflow (Expenses)', Value: activityData.kpis.total_expense },
                { Metric: 'Net Daily Cash Flow', Value: activityData.kpis.net_flow },
                { Metric: 'Active Workforce Present', Value: activityData.kpis.workforce_count },
                { Metric: 'Employee / Labour Wages', Value: activityData.kpis.labour_expense },
                { Metric: 'Vendor Remittances', Value: activityData.kpis.vendor_expense },
                { Metric: 'Petty Cash Outflow', Value: activityData.kpis.petty_cash_expense },
                { Metric: 'Material Procurement', Value: activityData.kpis.material_expense }
            ];
            const wsSummary = XLSX.utils.json_to_sheet(summarySheetData);
            XLSX.utils.book_append_sheet(wb, wsSummary, 'Daily Summary');

            // Income Sheet
            if (activityData.records?.income?.length > 0) {
                const incData = activityData.records.income.map(i => ({
                    'Date': i.payment_date,
                    'Invoice No': i.invoice_no,
                    'Client Name': i.client_name,
                    'Project / Site': i.project_name,
                    'Payment Mode': i.payment_mode,
                    'Amount (INR)': i.amount,
                    'TDS Deducted': i.tds_deducted,
                    'Reference': i.transaction_ref,
                    'Notes': i.notes
                }));
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(incData), 'Daily Inflow');
            }

            // Employee / Labour Sheet
            if (activityData.records?.employee_labour?.length > 0) {
                const empData = activityData.records.employee_labour.map(e => ({
                    'Date': e.date,
                    'Employee / Worker': e.employee_name,
                    'Site / Project': e.project_name,
                    'Status': e.status,
                    'In Time': e.in_time || '-',
                    'Out Time': e.out_time || '-',
                    'Workload': e.workload,
                    'Pay Type': e.pay_type,
                    'Applied Rate (INR)': e.applied_rate,
                    'Daily Wage (INR)': e.amount,
                    'Notes': e.notes
                }));
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(empData), 'Labour & Attendance');
            }

            // Vendor Payments Sheet
            if (activityData.records?.vendor_payments?.length > 0) {
                const venData = activityData.records.vendor_payments.map(v => ({
                    'Date': v.payment_date,
                    'Vendor': v.vendor_name,
                    'Invoice No': v.invoice_no,
                    'Site / Project': v.project_name,
                    'Payment Mode': v.payment_method,
                    'Amount (INR)': v.amount,
                    'Notes': v.notes
                }));
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(venData), 'Vendor Payments');
            }

            // Petty Cash Sheet
            if (activityData.records?.petty_cash?.length > 0) {
                const petData = activityData.records.petty_cash.map(p => ({
                    'Date': p.date,
                    'Category': p.category,
                    'Remarks': p.remarks,
                    'Staff / User': p.staff_name,
                    'Site / Project': p.project_name,
                    'Amount (INR)': p.amount,
                    'Status': p.status
                }));
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(petData), 'Petty Cash');
            }

            // Material Spent Sheet
            if (activityData.records?.material_spent?.length > 0) {
                const matData = activityData.records.material_spent.map(m => ({
                    'Date': m.date,
                    'Supplier / Biller': m.biller_name,
                    'Items Summary': m.items_summary,
                    'Site / Project': m.project_name,
                    'Payment Method': m.payment_method,
                    'Paid By': m.paid_by,
                    'Amount (INR)': m.amount
                }));
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(matData), 'Material Spent');
            }

            XLSX.writeFile(wb, `Daily_Activity_${selectedDate}_${selectedProject}.xlsx`);
        } catch (err) {
            console.error('Error exporting daily Excel:', err);
            alert('Failed to export daily Excel.');
        }
    };

    const handlePrint = () => {
        window.print();
    };

    // Calculate spend breakdown percentages
    const totalExp = activityData.kpis.total_expense || 0;
    const labourPct = totalExp > 0 ? Math.round((activityData.kpis.labour_expense / totalExp) * 100) : 0;
    const vendorPct = totalExp > 0 ? Math.round((activityData.kpis.vendor_expense / totalExp) * 100) : 0;
    const pettyPct = totalExp > 0 ? Math.round((activityData.kpis.petty_cash_expense / totalExp) * 100) : 0;
    const materialPct = totalExp > 0 ? Math.round((activityData.kpis.material_expense / totalExp) * 100) : 0;

    return (
        <div className="space-y-8 animate-in fade-in duration-500 pb-24 print:p-0 print:space-y-4">
            {/* =========================================================================
                1. TOP HEADER & INTERACTIVE FILTER CONTROLS
            ========================================================================= */}
            <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm print:hidden">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    {/* Title & Metadata */}
                    <div>
                        <div className="flex items-center gap-2 text-indigo-600 mb-1">
                            <span className="p-2 bg-indigo-50 rounded-xl text-indigo-600">
                                <Activity size={20} />
                            </span>
                            <span className="text-[11px] font-black uppercase tracking-[0.2em]">
                                Daily Site Activity & Financial Audit
                            </span>
                        </div>
                        <h1 className="text-3xl lg:text-4xl font-black text-slate-900 tracking-tight italic">
                            Daily Site Activity
                        </h1>
                        <div className="flex flex-wrap items-center gap-2 text-slate-500 font-bold text-xs uppercase tracking-wider mt-1.5">
                            <span className="text-slate-800 font-black">{user?.company_name || 'ClientOrbit Pro'}</span>
                            <span>•</span>
                            <span className="text-indigo-600 font-black">{formatDisplayDate(selectedDate)}</span>
                            {isYesterday && (
                                <span className="px-2.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-black rounded-full uppercase tracking-wider">
                                    Yesterday's Log
                                </span>
                            )}
                            {isToday && (
                                <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-black rounded-full uppercase tracking-wider">
                                    Today's Active
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Controls Bar: Quick Presets, Stepper, Date Picker, Site Select */}
                    <div className="flex flex-wrap items-center gap-3">
                        {/* QUICK PRESETS: Yesterday & Today */}
                        <div className="flex items-center bg-slate-100 p-1.5 rounded-2xl border border-slate-200/60 shadow-inner">
                            <button
                                onClick={handleSetYesterday}
                                className={`px-4 py-2 rounded-xl text-xs font-black uppercase transition-all duration-200 ${
                                    isYesterday
                                        ? 'bg-slate-900 text-white shadow-md shadow-slate-900/20 scale-[1.02]'
                                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                                }`}
                            >
                                Yesterday
                            </button>
                            <button
                                onClick={handleSetToday}
                                className={`px-4 py-2 rounded-xl text-xs font-black uppercase transition-all duration-200 ${
                                    isToday
                                        ? 'bg-slate-900 text-white shadow-md shadow-slate-900/20 scale-[1.02]'
                                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                                }`}
                            >
                                Today
                            </button>
                        </div>

                        {/* DAY STEPPER + DATE INPUT */}
                        <div className="flex items-center bg-white border border-slate-200/80 rounded-2xl shadow-sm p-1">
                            <button
                                onClick={handlePrevDay}
                                title="Previous Day"
                                className="p-2 hover:bg-slate-100 rounded-xl text-slate-600 hover:text-slate-900 transition-colors"
                            >
                                <ChevronLeft size={18} />
                            </button>
                            <div className="flex items-center gap-2 px-2">
                                <Calendar size={15} className="text-indigo-600" />
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="text-xs font-black text-slate-800 bg-transparent outline-none cursor-pointer"
                                />
                            </div>
                            <button
                                onClick={handleNextDay}
                                title="Next Day"
                                className="p-2 hover:bg-slate-100 rounded-xl text-slate-600 hover:text-slate-900 transition-colors"
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>

                        {/* SITE / PROJECT SELECTOR DROPDOWN */}
                        <div className="relative">
                            <select
                                value={selectedProject}
                                onChange={(e) => setSelectedProject(e.target.value)}
                                className="flex items-center gap-2 px-4 py-3 bg-white border border-slate-200/80 rounded-2xl text-xs font-black uppercase tracking-wider text-slate-800 shadow-sm outline-none cursor-pointer hover:border-indigo-400 transition-colors"
                            >
                                <option value="all">🏢 All Sites / Projects</option>
                                {projectsList.map(p => (
                                    <option key={p.id} value={p.id}>
                                        📍 {p.project_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* REFRESH, EXCEL, PRINT */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleRefresh}
                                disabled={loading || refreshing}
                                title="Refresh Day Data"
                                className="p-3 bg-white hover:bg-slate-50 border border-slate-200/80 rounded-2xl text-slate-600 hover:text-slate-900 shadow-sm transition-all"
                            >
                                <RefreshCw size={16} className={refreshing || loading ? 'animate-spin text-indigo-600' : ''} />
                            </button>
                            <button
                                onClick={handleExportExcel}
                                title="Export Daily Ledger to Excel"
                                className="flex items-center gap-2 px-4 py-3 bg-white hover:bg-slate-50 border border-slate-200/80 rounded-2xl text-xs font-black uppercase tracking-wider text-slate-700 shadow-sm transition-all"
                            >
                                <Download size={14} className="text-emerald-600" />
                                <span className="hidden sm:inline">Excel</span>
                            </button>
                            <button
                                onClick={handlePrint}
                                title="Print Daily Report"
                                className="p-3 bg-white hover:bg-slate-50 border border-slate-200/80 rounded-2xl text-slate-600 hover:text-slate-900 shadow-sm transition-all"
                            >
                                <Printer size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* =========================================================================
                2. DAILY KPI SUMMARY CARDS
            ========================================================================= */}
            {loading && !refreshing ? (
                <div className="bg-white border border-slate-100 rounded-[32px] p-16 flex flex-col items-center justify-center text-center shadow-sm">
                    <Loader2 size={40} className="animate-spin text-indigo-600 mb-4" />
                    <h3 className="text-lg font-black text-slate-900">Auditing Daily Site Activity...</h3>
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                        Extracting income, attendance, vendor debits, petty cash & material logs for {selectedDate}
                    </p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* 1. Daily Inflow (Income) */}
                        <div className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                                    Daily Inflow (Income)
                                </span>
                                <div className="p-2.5 bg-emerald-50 rounded-2xl text-emerald-600">
                                    <ArrowDownCircle size={22} />
                                </div>
                            </div>
                            <div className="text-3xl font-black text-slate-900 tracking-tight">
                                {formatINR(activityData.kpis.total_income)}
                            </div>
                            <div className="mt-4 flex items-center justify-between pt-3 border-t border-slate-50 text-xs font-bold text-slate-500">
                                <span>Client Receipts</span>
                                <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-black text-[10px]">
                                    {activityData.kpis.counts?.income_records || 0} Records
                                </span>
                            </div>
                        </div>

                        {/* 2. Daily Outflow (Total Expenses) */}
                        <div className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                                    Daily Outflow (Expenses)
                                </span>
                                <div className="p-2.5 bg-rose-50 rounded-2xl text-rose-600">
                                    <ArrowUpCircle size={22} />
                                </div>
                            </div>
                            <div className="text-3xl font-black text-slate-900 tracking-tight">
                                {formatINR(activityData.kpis.total_expense)}
                            </div>
                            <div className="mt-4 flex items-center justify-between pt-3 border-t border-slate-50 text-xs font-bold text-slate-500">
                                <span>Total Debits</span>
                                <span className="px-2 py-0.5 bg-rose-50 text-rose-700 rounded-full font-black text-[10px]">
                                    {activityData.kpis.counts?.total_expense_records || 0} Transactions
                                </span>
                            </div>
                        </div>

                        {/* 3. Net Daily Cash Flow */}
                        <div className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                                    Net Daily Flow
                                </span>
                                <div className={`p-2.5 rounded-2xl ${
                                    activityData.kpis.net_flow >= 0 ? 'bg-indigo-50 text-indigo-600' : 'bg-amber-50 text-amber-600'
                                }`}>
                                    <Wallet size={22} />
                                </div>
                            </div>
                            <div className={`text-3xl font-black tracking-tight ${
                                activityData.kpis.net_flow >= 0 ? 'text-emerald-600' : 'text-rose-600'
                            }`}>
                                {formatINR(activityData.kpis.net_flow)}
                            </div>
                            <div className="mt-4 flex items-center justify-between pt-3 border-t border-slate-50 text-xs font-bold text-slate-500">
                                <span>Day Balance</span>
                                <span className={`px-2 py-0.5 rounded-full font-black text-[10px] ${
                                    activityData.kpis.net_flow >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                                }`}>
                                    {activityData.kpis.net_flow >= 0 ? '+ Surplus' : '- Outflow Deficit'}
                                </span>
                            </div>
                        </div>

                        {/* 4. Active On-Site Workforce */}
                        <div className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                                    Workforce On Site
                                </span>
                                <div className="p-2.5 bg-purple-50 rounded-2xl text-purple-600">
                                    <HardHat size={22} />
                                </div>
                            </div>
                            <div className="text-3xl font-black text-slate-900 tracking-tight">
                                {activityData.kpis.workforce_count || 0} <span className="text-sm font-bold text-slate-400">Workers</span>
                            </div>
                            <div className="mt-4 flex items-center justify-between pt-3 border-t border-slate-50 text-xs font-bold text-slate-500">
                                <span>Day Wages</span>
                                <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full font-black text-[10px]">
                                    {formatINR(activityData.kpis.labour_expense)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* =========================================================================
                        3. TAB NAVIGATION BAR & SEARCH
                    ========================================================================= */}
                    <div className="bg-white border border-slate-100 rounded-[32px] p-3 shadow-sm print:hidden">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            {/* Tab Pills */}
                            <div className="flex items-center gap-1.5 overflow-x-auto pb-2 lg:pb-0 scrollbar-none">
                                {[
                                    { id: 'overview', label: 'Overview', icon: Activity, count: null },
                                    { id: 'income', label: 'Income', icon: ArrowDownCircle, count: activityData.kpis.counts?.income_records, color: 'text-emerald-600' },
                                    { id: 'expenses', label: 'All Expenses', icon: ArrowUpCircle, count: activityData.kpis.counts?.total_expense_records, color: 'text-rose-600' },
                                    { id: 'employee', label: 'Employee / Labour', icon: HardHat, count: activityData.kpis.counts?.employee_records, color: 'text-purple-600' },
                                    { id: 'vendor', label: 'Vendor Payments', icon: Truck, count: activityData.kpis.counts?.vendor_records, color: 'text-indigo-600' },
                                    { id: 'petty', label: 'Petty Cash', icon: Wallet, count: activityData.kpis.counts?.petty_records, color: 'text-emerald-600' },
                                    { id: 'material', label: 'Material Spent', icon: ShoppingCart, count: activityData.kpis.counts?.material_records, color: 'text-amber-600' },
                                ].map(tab => {
                                    const Icon = tab.icon;
                                    const isActive = activeTab === tab.id;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => { setActiveTab(tab.id); setTableSearch(''); }}
                                            className={`flex items-center gap-2 px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-wider whitespace-nowrap transition-all duration-200 ${
                                                isActive
                                                    ? 'bg-slate-900 text-white shadow-md shadow-slate-900/10'
                                                    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                                            }`}
                                        >
                                            <Icon size={16} className={isActive ? 'text-white' : tab.color || 'text-slate-400'} />
                                            <span>{tab.label}</span>
                                            {tab.count !== null && tab.count !== undefined && (
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                                                    isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                                                }`}>
                                                    {tab.count}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Search bar inside active tab */}
                            {activeTab !== 'overview' && (
                                <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200/80 rounded-2xl w-full lg:w-72">
                                    <Search size={16} className="text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder={`Filter ${activeTab} records...`}
                                        value={tableSearch}
                                        onChange={(e) => setTableSearch(e.target.value)}
                                        className="bg-transparent text-xs font-bold text-slate-800 placeholder-slate-400 outline-none w-full"
                                    />
                                    {tableSearch && (
                                        <button onClick={() => setTableSearch('')} className="text-slate-400 hover:text-slate-600">
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* =========================================================================
                        4. TAB CONTENT PANELS
                    ========================================================================= */}

                    {/* TAB: OVERVIEW */}
                    {activeTab === 'overview' && (
                        <div className="space-y-8">
                            {/* 4 HIGHLIGHT CARDS: Labour, Vendors, Petty Cash, Material */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {/* Employee / Labour */}
                                <div
                                    onClick={() => setActiveTab('employee')}
                                    className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm hover:shadow-md cursor-pointer transition-all group"
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="p-2.5 bg-purple-50 text-purple-600 rounded-2xl group-hover:scale-110 transition-transform">
                                            <HardHat size={20} />
                                        </span>
                                        <span className="text-[11px] font-black px-2.5 py-1 rounded-full bg-purple-50 text-purple-700">
                                            {labourPct}% of day spend
                                        </span>
                                    </div>
                                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-400">Employee / Labour</h4>
                                    <div className="text-2xl font-black text-slate-900 mt-1">
                                        {formatINR(activityData.kpis.labour_expense)}
                                    </div>
                                    <div className="mt-3 text-xs font-bold text-slate-500 flex items-center justify-between">
                                        <span>Workforce Active:</span>
                                        <span className="text-purple-600 font-black">{activityData.kpis.workforce_count} Present</span>
                                    </div>
                                </div>

                                {/* Vendor Payments */}
                                <div
                                    onClick={() => setActiveTab('vendor')}
                                    className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm hover:shadow-md cursor-pointer transition-all group"
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="p-2.5 bg-indigo-50 text-indigo-600 rounded-2xl group-hover:scale-110 transition-transform">
                                            <Truck size={20} />
                                        </span>
                                        <span className="text-[11px] font-black px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700">
                                            {vendorPct}% of day spend
                                        </span>
                                    </div>
                                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-400">Vendor Payments</h4>
                                    <div className="text-2xl font-black text-slate-900 mt-1">
                                        {formatINR(activityData.kpis.vendor_expense)}
                                    </div>
                                    <div className="mt-3 text-xs font-bold text-slate-500 flex items-center justify-between">
                                        <span>Bills Remitted:</span>
                                        <span className="text-indigo-600 font-black">{activityData.kpis.counts?.vendor_records || 0} Bills</span>
                                    </div>
                                </div>

                                {/* Petty Cash */}
                                <div
                                    onClick={() => setActiveTab('petty')}
                                    className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm hover:shadow-md cursor-pointer transition-all group"
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="p-2.5 bg-emerald-50 text-emerald-600 rounded-2xl group-hover:scale-110 transition-transform">
                                            <Wallet size={20} />
                                        </span>
                                        <span className="text-[11px] font-black px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">
                                            {pettyPct}% of day spend
                                        </span>
                                    </div>
                                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-400">Petty Cash</h4>
                                    <div className="text-2xl font-black text-slate-900 mt-1">
                                        {formatINR(activityData.kpis.petty_cash_expense)}
                                    </div>
                                    <div className="mt-3 text-xs font-bold text-slate-500 flex items-center justify-between">
                                        <span>Site Debits:</span>
                                        <span className="text-emerald-600 font-black">{activityData.kpis.counts?.petty_records || 0} Logs</span>
                                    </div>
                                </div>

                                {/* Material Spent */}
                                <div
                                    onClick={() => setActiveTab('material')}
                                    className="bg-white border border-slate-100 rounded-[32px] p-6 shadow-sm hover:shadow-md cursor-pointer transition-all group"
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="p-2.5 bg-amber-50 text-amber-600 rounded-2xl group-hover:scale-110 transition-transform">
                                            <ShoppingCart size={20} />
                                        </span>
                                        <span className="text-[11px] font-black px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">
                                            {materialPct}% of day spend
                                        </span>
                                    </div>
                                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-400">Material Spent</h4>
                                    <div className="text-2xl font-black text-slate-900 mt-1">
                                        {formatINR(activityData.kpis.material_expense)}
                                    </div>
                                    <div className="mt-3 text-xs font-bold text-slate-500 flex items-center justify-between">
                                        <span>Direct Purchases:</span>
                                        <span className="text-amber-600 font-black">{activityData.kpis.counts?.material_records || 0} Bills</span>
                                    </div>
                                </div>
                            </div>

                            {/* SITE BREAKDOWN FOR THIS DAY (When viewing All Sites or specific site) */}
                            {activityData.records?.site_breakdown?.length > 0 && (
                                <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm">
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                                Site Activity Matrix for {formatDisplayDate(selectedDate)}
                                            </h3>
                                            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                                                Breakdown of day's inflows, costs & workers across active sites
                                            </p>
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse">
                                            <thead>
                                                <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                    <th className="py-4 px-4">Site / Project</th>
                                                    <th className="py-4 px-4 text-center">Workforce Present</th>
                                                    <th className="py-4 px-4 text-right">Income</th>
                                                    <th className="py-4 px-4 text-right">Labour Wages</th>
                                                    <th className="py-4 px-4 text-right">Vendor Remittance</th>
                                                    <th className="py-4 px-4 text-right">Petty Cash</th>
                                                    <th className="py-4 px-4 text-right">Materials</th>
                                                    <th className="py-4 px-4 text-right">Total Outflow</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-50 text-xs font-bold text-slate-700">
                                                {activityData.records.site_breakdown.map((site) => (
                                                    <tr key={site.project_id} className="hover:bg-slate-50/80 transition-colors">
                                                        <td className="py-4 px-4">
                                                            <div className="flex items-center gap-2">
                                                                <span className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                                                                    <Building2 size={14} />
                                                                </span>
                                                                <span className="font-black text-slate-900">{site.project_name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-4 text-center">
                                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black ${
                                                                site.workforce_count > 0 ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-500'
                                                            }`}>
                                                                {site.workforce_count} Workers
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-4 text-right font-black text-emerald-600">
                                                            {site.income > 0 ? formatINR(site.income) : '-'}
                                                        </td>
                                                        <td className="py-4 px-4 text-right font-bold text-purple-600">
                                                            {site.labour > 0 ? formatINR(site.labour) : '-'}
                                                        </td>
                                                        <td className="py-4 px-4 text-right font-bold text-indigo-600">
                                                            {site.vendor > 0 ? formatINR(site.vendor) : '-'}
                                                        </td>
                                                        <td className="py-4 px-4 text-right font-bold text-emerald-600">
                                                            {site.petty_cash > 0 ? formatINR(site.petty_cash) : '-'}
                                                        </td>
                                                        <td className="py-4 px-4 text-right font-bold text-amber-600">
                                                            {site.material > 0 ? formatINR(site.material) : '-'}
                                                        </td>
                                                        <td className="py-4 px-4 text-right font-black text-slate-900">
                                                            {formatINR(site.total_expense)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* CHRONOLOGICAL DAILY AUDIT FEED */}
                            <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm">
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                            Chronological Daily Entry Stream
                                        </h3>
                                        <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                                            All events entered or logged on {formatDisplayDate(selectedDate)} in one unified timeline
                                        </p>
                                    </div>
                                    <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full font-black text-xs">
                                        {(activityData.records?.income?.length || 0) + (activityData.records?.all_expenses?.length || 0)} Total Events
                                    </span>
                                </div>

                                {((activityData.records?.income?.length || 0) + (activityData.records?.all_expenses?.length || 0)) === 0 ? (
                                    <div className="text-center py-16 px-4">
                                        <div className="p-4 bg-slate-50 text-slate-400 rounded-full inline-flex mb-3">
                                            <Calendar size={32} />
                                        </div>
                                        <h4 className="text-base font-black text-slate-800">No Transactions Recorded on this Date</h4>
                                        <p className="text-xs text-slate-400 font-bold max-w-sm mx-auto mt-1">
                                            No client receipts, labour punches, vendor payments, petty cash or material purchases were entered for {selectedDate}. Use the quick presets above to view yesterday's records or step to another day.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {/* Income items */}
                                        {(activityData.records?.income || []).map((inc) => (
                                            <div key={`stream_inc_${inc.id}`} className="flex items-center justify-between p-4 rounded-2xl bg-emerald-50/50 border border-emerald-100/80 hover:bg-emerald-50 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <span className="p-2.5 bg-emerald-100 text-emerald-700 rounded-xl">
                                                        <ArrowDownCircle size={18} />
                                                    </span>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-md bg-emerald-200/60 text-emerald-800">
                                                                Income Receipt
                                                            </span>
                                                            <span className="font-black text-slate-900 text-sm">{inc.client_name}</span>
                                                        </div>
                                                        <p className="text-xs text-slate-500 font-bold mt-0.5">
                                                            {inc.project_name} • Invoice #{inc.invoice_no} • {inc.payment_mode}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-base font-black text-emerald-600">
                                                        +{formatINR(inc.amount)}
                                                    </div>
                                                    <span className="text-[10px] font-bold text-slate-400">Client Inflow</span>
                                                </div>
                                            </div>
                                        ))}

                                        {/* All expense items */}
                                        {(activityData.records?.all_expenses || []).map((exp) => {
                                            const isLabour = exp.category === 'Employee / Labour';
                                            const isVendor = exp.category === 'Vendor Payment';
                                            const isPetty = exp.category === 'Petty Cash';
                                            const isMaterial = exp.category === 'Material Spent';

                                            const badgeColor = isLabour
                                                ? 'bg-purple-100 text-purple-800'
                                                : isVendor
                                                ? 'bg-indigo-100 text-indigo-800'
                                                : isPetty
                                                ? 'bg-emerald-100 text-emerald-800'
                                                : 'bg-amber-100 text-amber-800';

                                            const iconColor = isLabour
                                                ? 'bg-purple-50 text-purple-600'
                                                : isVendor
                                                ? 'bg-indigo-50 text-indigo-600'
                                                : isPetty
                                                ? 'bg-emerald-50 text-emerald-600'
                                                : 'bg-amber-50 text-amber-600';

                                            const Icon = isLabour ? HardHat : isVendor ? Truck : isPetty ? Wallet : ShoppingCart;

                                            return (
                                                <div key={`stream_exp_${exp.id}`} className="flex items-center justify-between p-4 rounded-2xl bg-slate-50/60 border border-slate-100 hover:bg-slate-50 transition-colors">
                                                    <div className="flex items-center gap-3">
                                                        <span className={`p-2.5 rounded-xl ${iconColor}`}>
                                                            <Icon size={18} />
                                                        </span>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md ${badgeColor}`}>
                                                                    {exp.category}
                                                                </span>
                                                                <span className="font-black text-slate-900 text-sm">{exp.entity_name}</span>
                                                            </div>
                                                            <p className="text-xs text-slate-500 font-bold mt-0.5">
                                                                {exp.project_name} • {exp.description || exp.payment_method}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-base font-black text-slate-900">
                                                            -{formatINR(exp.amount)}
                                                        </div>
                                                        <span className="text-[10px] font-bold text-slate-400">{exp.payment_method || 'Outflow'}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB: INCOME */}
                    {activeTab === 'income' && (
                        <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                        Client Income Received on {formatDisplayDate(selectedDate)}
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                                        Verification of billing receipts and payments cleared on this date
                                    </p>
                                </div>
                                <span className="text-sm font-black text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                                    Total: {formatINR(activityData.kpis.total_income)}
                                </span>
                            </div>

                            {filteredIncome.length === 0 ? (
                                <div className="text-center py-16">
                                    <ArrowDownCircle size={36} className="text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm font-black text-slate-700">No client income logged on this date</p>
                                    <p className="text-xs text-slate-400 mt-1">Check another date or select All Sites.</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                <th className="py-4 px-4">Invoice #</th>
                                                <th className="py-4 px-4">Client Name</th>
                                                <th className="py-4 px-4">Site / Project</th>
                                                <th className="py-4 px-4">Payment Mode</th>
                                                <th className="py-4 px-4">Ref / Notes</th>
                                                <th className="py-4 px-4 text-right">TDS</th>
                                                <th className="py-4 px-4 text-right">Amount Received</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50 text-xs font-bold text-slate-700">
                                            {filteredIncome.map((inc) => (
                                                <tr key={inc.id} className="hover:bg-slate-50/80 transition-colors">
                                                    <td className="py-4 px-4 font-black text-indigo-600">
                                                        #{inc.invoice_no}
                                                    </td>
                                                    <td className="py-4 px-4 font-black text-slate-900">
                                                        {inc.client_name}
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-500">
                                                        {inc.project_name}
                                                    </td>
                                                    <td className="py-4 px-4">
                                                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md text-[11px] font-bold">
                                                            {inc.payment_mode || 'Bank'}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-500 max-w-xs truncate">
                                                        {inc.transaction_ref ? `Ref: ${inc.transaction_ref}` : (inc.notes || '-')}
                                                    </td>
                                                    <td className="py-4 px-4 text-right text-slate-400">
                                                        {inc.tds_deducted > 0 ? formatINR(inc.tds_deducted) : '-'}
                                                    </td>
                                                    <td className="py-4 px-4 text-right font-black text-emerald-600 text-sm">
                                                        {formatINR(inc.amount)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB: ALL EXPENSES */}
                    {activeTab === 'expenses' && (
                        <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                        All Expenses Logged on {formatDisplayDate(selectedDate)}
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                                        Consolidated stream of labour, vendor bills, petty cash and direct materials
                                    </p>
                                </div>
                                <span className="text-sm font-black text-rose-600 bg-rose-50 px-3 py-1 rounded-full">
                                    Total: {formatINR(activityData.kpis.total_expense)}
                                </span>
                            </div>

                            {filteredAllExpenses.length === 0 ? (
                                <div className="text-center py-16">
                                    <ArrowUpCircle size={36} className="text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm font-black text-slate-700">No expenses recorded on this date</p>
                                    <p className="text-xs text-slate-400 mt-1">Try selecting yesterday or an active working day.</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                <th className="py-4 px-4">Category</th>
                                                <th className="py-4 px-4">Beneficiary / Entity</th>
                                                <th className="py-4 px-4">Site / Project</th>
                                                <th className="py-4 px-4">Payment Method</th>
                                                <th className="py-4 px-4">Description / Reference</th>
                                                <th className="py-4 px-4 text-right">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50 text-xs font-bold text-slate-700">
                                            {filteredAllExpenses.map((exp) => {
                                                const badgeColor = exp.category === 'Employee / Labour'
                                                    ? 'bg-purple-100 text-purple-800'
                                                    : exp.category === 'Vendor Payment'
                                                    ? 'bg-indigo-100 text-indigo-800'
                                                    : exp.category === 'Petty Cash'
                                                    ? 'bg-emerald-100 text-emerald-800'
                                                    : 'bg-amber-100 text-amber-800';

                                                return (
                                                    <tr key={exp.id} className="hover:bg-slate-50/80 transition-colors">
                                                        <td className="py-4 px-4">
                                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${badgeColor}`}>
                                                                {exp.category}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-4 font-black text-slate-900">
                                                            {exp.entity_name}
                                                        </td>
                                                        <td className="py-4 px-4 text-slate-500">
                                                            {exp.project_name}
                                                        </td>
                                                        <td className="py-4 px-4 text-slate-600">
                                                            {exp.payment_method || '-'}
                                                        </td>
                                                        <td className="py-4 px-4 text-slate-500 max-w-sm truncate">
                                                            {exp.description}
                                                        </td>
                                                        <td className="py-4 px-4 text-right font-black text-slate-900 text-sm">
                                                            {formatINR(exp.amount)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB: EMPLOYEE / LABOUR */}
                    {activeTab === 'employee' && (
                        <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                        Worker Attendance & Daily Wages on {formatDisplayDate(selectedDate)}
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                                        Daily shift multipliers, in/out punch times, rates, and computed day wages
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-black text-purple-700 bg-purple-50 px-3 py-1 rounded-full">
                                        {activityData.kpis.workforce_count} Present
                                    </span>
                                    <span className="text-sm font-black text-purple-700 bg-purple-100 px-3 py-1 rounded-full">
                                        Wages: {formatINR(activityData.kpis.labour_expense)}
                                    </span>
                                </div>
                            </div>

                            {filteredEmployeeLabour.length === 0 ? (
                                <div className="text-center py-16">
                                    <HardHat size={36} className="text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm font-black text-slate-700">No attendance or labour wages recorded for this date</p>
                                    <p className="text-xs text-slate-400 mt-1">Ensure worker attendance has been marked in Site Operations.</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                <th className="py-4 px-4">Employee / Worker</th>
                                                <th className="py-4 px-4">Site / Project</th>
                                                <th className="py-4 px-4 text-center">Status</th>
                                                <th className="py-4 px-4 text-center">Punch Timings</th>
                                                <th className="py-4 px-4 text-center">Workload</th>
                                                <th className="py-4 px-4 text-right">Wage Rate</th>
                                                <th className="py-4 px-4 text-right">Day Wage</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50 text-xs font-bold text-slate-700">
                                            {filteredEmployeeLabour.map((emp) => (
                                                <tr key={emp.id} className="hover:bg-slate-50/80 transition-colors">
                                                    <td className="py-4 px-4 font-black text-slate-900">
                                                        <div className="flex items-center gap-2">
                                                            <span className="p-1.5 bg-purple-50 text-purple-600 rounded-lg">
                                                                <HardHat size={14} />
                                                            </span>
                                                            <span>{emp.employee_name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-600">
                                                        {emp.project_name}
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${
                                                            emp.status === 'Present'
                                                                ? 'bg-emerald-100 text-emerald-800'
                                                                : emp.status === 'Half Day'
                                                                ? 'bg-amber-100 text-amber-800'
                                                                : emp.status === 'Absent'
                                                                ? 'bg-rose-100 text-rose-800'
                                                                : 'bg-indigo-100 text-indigo-800'
                                                        }`}>
                                                            {emp.status || 'Present'}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-center text-slate-500 font-mono text-[11px]">
                                                        {emp.in_time ? (
                                                            <span>{emp.in_time} {emp.out_time ? `→ ${emp.out_time}` : ''}</span>
                                                        ) : (
                                                            <span className="text-slate-300">-</span>
                                                        )}
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <span className="px-2 py-0.5 bg-slate-100 rounded-md font-bold text-slate-700">
                                                            {emp.workload || '1 Shift'}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-right text-slate-500">
                                                        {emp.applied_rate > 0 ? `${formatINR(emp.applied_rate)} / ${emp.pay_type}` : '-'}
                                                    </td>
                                                    <td className="py-4 px-4 text-right font-black text-purple-700 text-sm">
                                                        {formatINR(emp.amount)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB: VENDOR PAYMENTS */}
                    {activeTab === 'vendor' && (
                        <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                        Vendor Payments Disbursed on {formatDisplayDate(selectedDate)}
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                                        Remittances to contractors, hardware suppliers, and trade partners
                                    </p>
                                </div>
                                <span className="text-sm font-black text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
                                    Total: {formatINR(activityData.kpis.vendor_expense)}
                                </span>
                            </div>

                            {filteredVendorPayments.length === 0 ? (
                                <div className="text-center py-16">
                                    <Truck size={36} className="text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm font-black text-slate-700">No vendor remittances logged on this date</p>
                                    <p className="text-xs text-slate-400 mt-1">No supplier payments were recorded on {selectedDate}.</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                <th className="py-4 px-4">Vendor Partner</th>
                                                <th className="py-4 px-4">Invoice #</th>
                                                <th className="py-4 px-4">Site / Project</th>
                                                <th className="py-4 px-4">Payment Method</th>
                                                <th className="py-4 px-4">Notes / Remarks</th>
                                                <th className="py-4 px-4 text-right">Amount Paid</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50 text-xs font-bold text-slate-700">
                                            {filteredVendorPayments.map((ven) => (
                                                <tr key={ven.id} className="hover:bg-slate-50/80 transition-colors">
                                                    <td className="py-4 px-4 font-black text-slate-900">
                                                        {ven.vendor_name}
                                                    </td>
                                                    <td className="py-4 px-4 font-black text-indigo-600">
                                                        #{ven.invoice_no}
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-500">
                                                        {ven.project_name}
                                                    </td>
                                                    <td className="py-4 px-4">
                                                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md text-[11px] font-bold">
                                                            {ven.payment_method || 'Bank'}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-500 max-w-sm truncate">
                                                        {ven.notes || '-'}
                                                    </td>
                                                    <td className="py-4 px-4 text-right font-black text-indigo-600 text-sm">
                                                        {formatINR(ven.amount)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB: PETTY CASH */}
                    {activeTab === 'petty' && (
                        <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                        Site Petty Cash Debits on {formatDisplayDate(selectedDate)}
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                                        Day-to-day site expenses, transport, food, refreshments, and fast fixes
                                    </p>
                                </div>
                                <span className="text-sm font-black text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                                    Total: {formatINR(activityData.kpis.petty_cash_expense)}
                                </span>
                            </div>

                            {filteredPettyCash.length === 0 ? (
                                <div className="text-center py-16">
                                    <Wallet size={36} className="text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm font-black text-slate-700">No petty cash debits recorded on this date</p>
                                    <p className="text-xs text-slate-400 mt-1">No site expenses were submitted on {selectedDate}.</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                <th className="py-4 px-4">Category</th>
                                                <th className="py-4 px-4">Staff / Spender</th>
                                                <th className="py-4 px-4">Site / Project</th>
                                                <th className="py-4 px-4">Remarks / Purpose</th>
                                                <th className="py-4 px-4 text-center">Status</th>
                                                <th className="py-4 px-4 text-right">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50 text-xs font-bold text-slate-700">
                                            {filteredPettyCash.map((pc) => (
                                                <tr key={pc.id} className="hover:bg-slate-50/80 transition-colors">
                                                    <td className="py-4 px-4">
                                                        <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full font-black text-[10px] uppercase">
                                                            {pc.category || 'General'}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 font-black text-slate-900">
                                                        {pc.staff_name}
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-500">
                                                        {pc.project_name}
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-600 max-w-sm truncate">
                                                        {pc.remarks}
                                                    </td>
                                                    <td className="py-4 px-4 text-center">
                                                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[10px] font-bold uppercase">
                                                            {pc.status || 'Approved'}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-right font-black text-emerald-600 text-sm">
                                                        {formatINR(pc.amount)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB: MATERIAL SPENT */}
                    {activeTab === 'material' && (
                        <div className="bg-white border border-slate-100 rounded-[36px] p-6 lg:p-8 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                        Material Procured on {formatDisplayDate(selectedDate)}
                                    </h3>
                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
                                        Direct on-site material purchases, hardware, plywood, paint, and fittings
                                    </p>
                                </div>
                                <span className="text-sm font-black text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                                    Total: {formatINR(activityData.kpis.material_expense)}
                                </span>
                            </div>

                            {filteredMaterialSpent.length === 0 ? (
                                <div className="text-center py-16">
                                    <ShoppingCart size={36} className="text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm font-black text-slate-700">No material purchases recorded on this date</p>
                                    <p className="text-xs text-slate-400 mt-1">No direct site purchases were logged on {selectedDate}.</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                <th className="py-4 px-4">Supplier / Biller</th>
                                                <th className="py-4 px-4">Site / Project</th>
                                                <th className="py-4 px-4">Items Summary</th>
                                                <th className="py-4 px-4">Paid By</th>
                                                <th className="py-4 px-4">Payment Method</th>
                                                <th className="py-4 px-4 text-right">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50 text-xs font-bold text-slate-700">
                                            {filteredMaterialSpent.map((mat) => (
                                                <tr key={mat.id} className="hover:bg-slate-50/80 transition-colors">
                                                    <td className="py-4 px-4 font-black text-slate-900">
                                                        {mat.biller_name}
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-500">
                                                        {mat.project_name}
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-700 font-medium max-w-sm truncate">
                                                        {mat.items_summary}
                                                    </td>
                                                    <td className="py-4 px-4 text-slate-500">
                                                        {mat.paid_by || 'Staff'}
                                                    </td>
                                                    <td className="py-4 px-4">
                                                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md text-[11px] font-bold">
                                                            {mat.payment_method || 'Direct'}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-right font-black text-amber-600 text-sm">
                                                        {formatINR(mat.amount)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
